import React, { useState, useEffect, useRef } from "react"
import { graphql } from "gatsby"
import Link from "./link"
import CallToAction from "./call-to-action"
import _ from "lodash"
import { useInView } from "react-intersection-observer"

export const query = graphql`
  fragment FullWidthAnimationFields on DatoCmsFullwidthAnimation {
    id
    model {
      apiKey
    }
    title
    componentId: key
    blurbNode {
      childMarkdownRemark {
        html
      }
    }
    hideTextContent
    animationName: animation
    url
    urltext
    ctaType
    headline
  }
`

const FullWidthCanvasAnimation = ({
  headline,
  blurbNode,
  url,
  urltext,
  ctaType,
}: Queries.FullWidthAnimationFieldsFragment) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [images, setImages] = useState<HTMLImageElement[]>([])
  const [frameIndex, setFrameIndex] = useState(0)
  const [isMobile, setIsMobile] = useState<boolean | null>(null)
  const [windowSize, setWindowSize] = useState<{
    width: number | null
    height: number | null
  }>({
    width: null,
    height: null,
  })
  const frameCount = 86
  const mobileBreakpoint = 576
  const blurb =
    (blurbNode &&
      blurbNode.childMarkdownRemark &&
      blurbNode.childMarkdownRemark.html) ||
    ""

  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const getCurrentFrame = (index: number) => {
    return `/images/animations/power-of-nature/${
      isMobile ? "mobile" : "desktop"
    }/power_of_nature-final${index.toString().padStart(2, "0")}.jpg`
  }

  const preloadImages = (firstImage: HTMLImageElement) => {
    const preloadedImages: HTMLImageElement[] = []
    for (let i = 1; i <= frameCount; i++) {
      const img = new Image()
      const imgSrc = getCurrentFrame(i)
      img.src = imgSrc
      preloadedImages.push(img)
    }
    preloadedImages.unshift(firstImage)
    setImages(preloadedImages)
  }

  const handleScroll = () => {
    const viewportHeight = window.innerHeight
    const scrollTop = window.scrollY
    const elementOffsetTop = wrapperRef.current?.offsetTop || 0
    const elementHeight = canvasRef.current?.offsetHeight || 0

    const distance = scrollTop + viewportHeight - elementOffsetTop
    const percentage = Math.round(
      distance / ((viewportHeight + elementHeight) / 100),
    )

    const frameIndex = Math.min(frameCount, Math.max(0, percentage))

    if (frameIndex > 0 && frameIndex <= frameCount) {
      setFrameIndex(frameIndex)
    }
  }

  const renderCanvas = () => {
    const width = isMobile ? 414 : 1920
    const height = isMobile ? 233 : 1080
    const context = canvasRef.current?.getContext("2d")
    if (context) {
      context.canvas.width = width
      context.canvas.height = height
    }
  }

  const throttledScroll = _.throttle(handleScroll, 50)

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    window.addEventListener("resize", handleResize)
    window.addEventListener("scroll", throttledScroll)
    return () => {
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("scroll", throttledScroll)
    }
  }, [])

  useEffect(() => {
    if (inView) {
      const windowWidth = windowSize.width || window.innerWidth
      const isCurrentMobile = windowWidth && windowWidth <= mobileBreakpoint
      if (windowWidth && isCurrentMobile !== isMobile) {
        setIsMobile(isCurrentMobile)
      }
    }
  }, [windowSize, inView])

  useEffect(() => {
    if (isMobile !== null) {
      renderCanvas()
      const img = new Image()
      const imgSrc = getCurrentFrame(0)
      img.src = imgSrc
      img.onload = () => {
        setImages([img])
        preloadImages(img)
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (!canvasRef.current || images.length < 1) {
      return
    }

    const context = canvasRef.current.getContext("2d")

    const drawImage = () => {
      if (images[frameIndex] !== undefined) {
        context.drawImage(images[frameIndex], 0, 0)
      }
    }
    const requestId = window.requestAnimationFrame(drawImage)

    return () => window.cancelAnimationFrame(requestId)
  }, [frameIndex, images])

  return (
    <div
      ref={node => {
        wrapperRef.current = node
        inViewRef(node)
      }}
      className="c-full-width-canvas-animation o-row"
    >
      <canvas
        className="c-full-width-canvas-animation__canvas"
        ref={canvasRef}
      />
      <div className="o-wrapper c-fullwidth-teaser__container">
        <div className="c-fullwidth-teaser__container-content">
          <h2 className="c-heading--b c-fullwidth-teaser__title">{headline}</h2>
          <div
            className="c-fullwidth-teaser__container-content-blurb"
            dangerouslySetInnerHTML={{ __html: blurb }}
          />
          {url && urltext && ctaType === "button" && (
            <CallToAction url={url} color={"green"}>
              {urltext}
            </CallToAction>
          )}

          {url && urltext && ctaType === "text" && (
            <Link url={url}>{urltext}</Link>
          )}
        </div>
      </div>
    </div>
  )
}

export default FullWidthCanvasAnimation
